import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, PAYMENT_MODES } from '../utils/constants';
import { Search, Users, Settings, Trash2, FileText, Archive, ArchiveRestore } from 'lucide-react';

const EMPTY_FORM = {
  name: '', phone_number: '', email: '', notes: '',
  gstin: '', gst_legal_name: '', gst_address: '', pincode: '', state: '', state_code: '',
  tac_rate: '10',
};

// TAC (commission) rate is a whole-number percentage between 0 and 50 inclusive.
// Held in form state as a string; validated non-empty integer in range before Save.
const TAC_RATE_MIN = 0;
const TAC_RATE_MAX = 50;
const tacRateValid = (v) => {
  const s = String(v ?? '').trim();
  if (!/^\d+$/.test(s)) return false; // integer only — no decimals, sign, or blank
  const n = Number(s);
  return n >= TAC_RATE_MIN && n <= TAC_RATE_MAX;
};

// GSTIN format: 2-digit state code, 5 letters (PAN alpha), 4 digits, 1 letter,
// 1 entity digit (1-9/A-Z), literal 'Z', 1 checksum char. Validated only when
// the field is non-empty — GST details are optional for a partner.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Indian PIN code: 6 digits, first digit non-zero. Optional like the other
// GST details — validated only when non-empty.
const PINCODE_RE = /^[1-9][0-9]{5}$/;

// Standard 36 Indian states/UTs mapped to their GST state codes (the first two
// digits of a GSTIN). Codes 25 (Daman & Diu) and 28 (legacy Andhra Pradesh)
// are retired and intentionally omitted.
const GST_STATES = [
  { name: 'Jammu and Kashmir', code: '01' },
  { name: 'Himachal Pradesh', code: '02' },
  { name: 'Punjab', code: '03' },
  { name: 'Chandigarh', code: '04' },
  { name: 'Uttarakhand', code: '05' },
  { name: 'Haryana', code: '06' },
  { name: 'Delhi', code: '07' },
  { name: 'Rajasthan', code: '08' },
  { name: 'Uttar Pradesh', code: '09' },
  { name: 'Bihar', code: '10' },
  { name: 'Sikkim', code: '11' },
  { name: 'Arunachal Pradesh', code: '12' },
  { name: 'Nagaland', code: '13' },
  { name: 'Manipur', code: '14' },
  { name: 'Mizoram', code: '15' },
  { name: 'Tripura', code: '16' },
  { name: 'Meghalaya', code: '17' },
  { name: 'Assam', code: '18' },
  { name: 'West Bengal', code: '19' },
  { name: 'Jharkhand', code: '20' },
  { name: 'Odisha', code: '21' },
  { name: 'Chhattisgarh', code: '22' },
  { name: 'Madhya Pradesh', code: '23' },
  { name: 'Gujarat', code: '24' },
  { name: 'Dadra and Nagar Haveli and Daman and Diu', code: '26' },
  { name: 'Maharashtra', code: '27' },
  { name: 'Karnataka', code: '29' },
  { name: 'Goa', code: '30' },
  { name: 'Lakshadweep', code: '31' },
  { name: 'Kerala', code: '32' },
  { name: 'Tamil Nadu', code: '33' },
  { name: 'Puducherry', code: '34' },
  { name: 'Andaman and Nicobar Islands', code: '35' },
  { name: 'Telangana', code: '36' },
  { name: 'Andhra Pradesh', code: '37' },
  { name: 'Ladakh', code: '38' },
];

const MAX_CERT_BYTES = 3 * 1024 * 1024; // 3MB

// redirectTo for the portal invite. This MUST point at the PUBLIC partner site's
// /partner/set-password page — NOT this internal app's origin — because that is
// where an invited partner lands to choose their password. Hardcoded to
// localhost for now; make this configurable / swap to the production public-site
// URL before shipping.
const PARTNER_SET_PASSWORD_URL = 'http://localhost:3000/partner/set-password';

// Effective approval status: old TAC partners and internally-added partners may
// have null/undefined approval_status — treat anything that isn't explicitly
// 'pending' or 'rejected' as approved so no partner disappears from the tabs.
const effStatus = (p) => {
  if (p.approval_status === 'pending') return 'pending';
  if (p.approval_status === 'rejected') return 'rejected';
  if (p.approval_status === 'suspended') return 'suspended';
  return 'approved';
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'archived', label: 'Archived' },
];

// Archiving is a SEPARATE axis from approval_status: archived_at set = hidden
// from every partner picker and name resolver, but the record (and everything
// linked to it by partners.id) stays intact. Any status can be archived.
const isArchived = (p) => !!p.archived_at;

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

// What is attached to a partner record by partners.id. Used to tell staff what
// archiving will leave linked, and to refuse a hard delete that would orphan
// or unlink any of it. Counts are head requests: no rows are transferred.
async function loadAttachments(partner) {
  const count = (table) =>
    supabase.from(table).select('id', { count: 'exact', head: true }).eq('partner_id', partner.id)
      .then((r) => (r.error ? null : (r.count || 0)));
  const [bookings, payments, holds, agents, documents] = await Promise.all([
    count('bookings'), count('partner_payments'), count('booking_holds'), count('partner_agents'), count('partner_documents'),
  ]);
  // A null count means the request itself failed; treat it as "unknown" rather
  // than zero so a delete is never allowed on the strength of a failed check.
  const failed = [bookings, payments, holds, agents, documents].some((v) => v === null);
  return {
    bookings: bookings || 0, payments: payments || 0, holds: holds || 0,
    agents: agents || 0, documents: documents || 0,
    login: !!partner.auth_user_id,
    failed,
  };
}
const hasAttachments = (a) => !!a && (a.failed || a.bookings > 0 || a.payments > 0 || a.holds > 0 || a.agents > 0 || a.documents > 0 || a.login);

// Plain-language list of what stays linked. Rendered inside the archive and
// delete confirmations.
function AttachmentSummary({ a, verb }) {
  if (!a) return null;
  if (a.failed) {
    return <div style={{ fontSize: '0.82rem', color: 'var(--color-danger-strong)' }}>Could not check what is linked to this partner. Try again.</div>;
  }
  const lines = [];
  const n = (c, one, many) => `${c} ${c === 1 ? one : many}`;
  if (a.bookings)  lines.push(`${n(a.bookings, 'booking is', 'bookings are')} linked to this partner and ${verb}.`);
  if (a.payments)  lines.push(`${n(a.payments, 'partner payment is', 'partner payments are')} recorded against it and ${verb}.`);
  if (a.holds)     lines.push(`${n(a.holds, 'booking hold', 'booking holds')} reference it.`);
  if (a.agents)    lines.push(`${n(a.agents, 'agent is', 'agents are')} listed under it.`);
  if (a.documents) lines.push(`${n(a.documents, 'document is', 'documents are')} uploaded for it.`);
  if (a.login)     lines.push('It has a partner portal login.');
  if (!lines.length) {
    return <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)' }}>Nothing is linked to this partner.</div>;
  }
  return (
    <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: '0.82rem', color: NAVY, textAlign: 'left', lineHeight: 1.5 }}>
      {lines.map((l) => <li key={l}>{l}</li>)}
    </ul>
  );
}

export default function Partners({ onNavigate }) {
  const [partners, setPartners] = useState([]);
  const [docsByPartner, setDocsByPartner] = useState({}); // { [partner_id]: [{ doc_type, file_path }] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', id? }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editHasCert, setEditHasCert] = useState(false); // partner already has a gst_cert doc
  const [certFile, setCertFile] = useState(null); // chosen File awaiting upload
  const [certError, setCertError] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // partner object
  const [deleteCheck, setDeleteCheck] = useState(null); // null (checking) | attachments for confirmDelete
  const [archiveModal, setArchiveModal] = useState(null); // null | partner object
  const [archiveCheck, setArchiveCheck] = useState(null); // null (checking) | attachments for archiveModal
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState(null); // partner id being unarchived
  const [prefModal, setPrefModal] = useState(null); // null | partner object
  const [prefForm, setPrefForm] = useState({ preferred_payment_mode: '', preferred_payment_status: '' });
  const [prefSaving, setPrefSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [approvingId, setApprovingId] = useState(null); // partner id being approved
  const [suspendingId, setSuspendingId] = useState(null); // partner id being suspended/unsuspended
  const [invitingId, setInvitingId] = useState(null); // partner id being invited to portal
  const [rejectModal, setRejectModal] = useState(null); // null | partner object
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);
  const [agents, setAgents] = useState([]); // active agents for the partner being edited
  const [agentName, setAgentName] = useState('');
  const [agentPhone, setAgentPhone] = useState(''); // digits only
  const [addingAgent, setAddingAgent] = useState(false);
  const [removingAgentId, setRemovingAgentId] = useState(null);
  const [bookingsModal, setBookingsModal] = useState(null); // null | partner object
  const [bookingsList, setBookingsList] = useState([]);
  const [bookingsAgentNames, setBookingsAgentNames] = useState({}); // { [agent_id]: agent_name }
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const toastTimer = useRef(null);
  const didSetDefaultTab = useRef(false);

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2800);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const load = async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('partners')
      .select('*')
      .order('name', { ascending: true });
    if (err) {
      setError('Failed to load partners');
      setLoading(false);
      return;
    }
    const list = data || [];
    setPartners(list);

    // Default the status tab once, on first load: pending-first if there's an
    // actionable queue, else show everything. Don't override the user's choice
    // on subsequent refreshes (after approve/reject).
    if (!didSetDefaultTab.current) {
      const hasPending = list.some((p) => effStatus(p) === 'pending');
      setStatusFilter(hasPending ? 'pending' : 'all');
      didSetDefaultTab.current = true;
    }

    // Load uploaded documents for pending partners so the card knows which
    // "View …" buttons to show without a per-card round-trip.
    const pendingIds = list.filter((p) => effStatus(p) === 'pending').map((p) => p.id);
    if (pendingIds.length) {
      const { data: docs } = await supabase
        .from('partner_documents')
        .select('partner_id, doc_type, file_path, uploaded_at')
        .in('partner_id', pendingIds);
      const map = {};
      (docs || []).forEach((d) => {
        if (!map[d.partner_id]) map[d.partner_id] = [];
        map[d.partner_id].push(d);
      });
      setDocsByPartner(map);
    } else {
      setDocsByPartner({});
    }

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError('');
    setCertFile(null);
    setCertError('');
    setEditHasCert(false);
    setModal({ mode: 'add' });
  };

  // Load the active agents (people at the partner company) for the edit modal.
  // Independent of the main partner Save — mirrors the certificate flow.
  const loadAgents = async (partnerId) => {
    const { data, error: err } = await supabase
      .from('partner_agents')
      .select('*')
      .eq('partner_id', partnerId)
      .eq('is_active', true)
      .order('created_at', { ascending: true });
    if (err) {
      console.error('partner_agents load failed:', err);
      setAgents([]);
      return;
    }
    setAgents(data || []);
  };

  const openEdit = async (partner) => {
    setForm({
      name: partner.name || '',
      phone_number: partner.phone_number || '',
      email: partner.email || '',
      notes: partner.notes || '',
      gstin: partner.gstin || '',
      gst_legal_name: partner.gst_legal_name || '',
      gst_address: partner.gst_address || '',
      pincode: partner.pincode || '',
      state: partner.state || '',
      state_code: partner.state_code || '',
      tac_rate: partner.tac_rate != null ? String(partner.tac_rate) : '10',
    });
    setError('');
    setCertFile(null);
    setCertError('');
    setEditHasCert(false);
    setAgents([]);
    setAgentName('');
    setAgentPhone('');
    setModal({ mode: 'edit', id: partner.id, name: partner.name });
    loadAgents(partner.id);
    // Does this partner already have a GST certificate on file? Drives the
    // "View GST Certificate" button. Non-fatal: on error we just hide the button.
    const { data: docRow } = await supabase
      .from('partner_documents')
      .select('file_path')
      .eq('partner_id', partner.id)
      .eq('doc_type', 'gst_cert')
      .order('uploaded_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setEditHasCert(!!(docRow && docRow.file_path));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const gstin = (form.gstin || '').trim().toUpperCase();
    // GSTIN is optional, but if entered it must be well-formed.
    if (gstin && !GSTIN_RE.test(gstin)) {
      setError('GSTIN is not valid. Leave it blank or enter a correct 15-character GSTIN.');
      return;
    }
    // Pincode is optional, but if entered it must be a valid 6-digit PIN.
    const pincode = (form.pincode || '').trim();
    if (pincode && !PINCODE_RE.test(pincode)) {
      setError('Pincode is not valid. Leave it blank or enter a 6-digit PIN code.');
      return;
    }
    // TAC rate must be a whole number 0–50.
    if (!tacRateValid(form.tac_rate)) {
      setError('TAC Rate must be a whole number between 0 and 50.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      phone_number: form.phone_number.trim() || null,
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
      gstin: gstin || null,
      gst_legal_name: form.gst_legal_name.trim() || null,
      gst_address: form.gst_address.trim() || null,
      pincode: pincode || null,
      state: form.state || null,
      state_code: form.state_code || null,
      tac_rate: Number(form.tac_rate),
    };
    if (modal.mode === 'add') {
      const { data: inserted, error: err } = await supabase
        .from('partners').insert(payload).select('id').single();
      if (err) { setError('Failed to save partner'); setSaving(false); return; }
      // Partner exists from here on — a cert upload failure must not undo that.
      if (certFile && inserted?.id) {
        const result = await uploadCertForPartner(inserted.id);
        if (!result.ok) {
          showToast('Partner created, but the GST certificate upload failed — you can add it via Edit.');
        }
      }
      setCertFile(null);
      setCertError('');
      setModal(null);
      load();
      setSaving(false);
      return;
    }
    const { error: err } = await supabase.from('partners').update(payload).eq('id', modal.id);
    if (err) { setError('Failed to save partner'); setSaving(false); return; }
    setModal(null);
    load();
    setSaving(false);
  };

  // Validate a chosen certificate client-side (type + size) before we hold onto it.
  const onCertFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) { setCertFile(null); setCertError(''); return; }
    const okType = ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)
      || /\.(pdf|jpe?g|png)$/i.test(f.name);
    if (!okType) {
      setCertFile(null);
      setCertError('File must be a PDF, JPG, or PNG.');
      return;
    }
    if (f.size > MAX_CERT_BYTES) {
      setCertFile(null);
      setCertError('File must be 3MB or smaller.');
      return;
    }
    setCertError('');
    setCertFile(f);
  };

  // Shared upload body: read the held certFile and send it to the
  // partner-doc-upload edge function for the given partner id. Used by the
  // edit-mode immediate upload AND the add-mode save (which uploads against
  // the freshly inserted id). Returns { ok, msg } — callers surface failure.
  const uploadCertForPartner = async (partnerId) => {
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Could not read file'));
        reader.readAsDataURL(certFile);
      });
      const { data, error: err } = await supabase.functions.invoke('partner-doc-upload', {
        body: {
          partnerId,
          docType: 'gst_cert',
          file: {
            data: dataUrl,
            filename: certFile.name,
            mimeType: certFile.type || 'application/octet-stream',
          },
        },
      });
      if (err || (data && data.error)) {
        return { ok: false, msg: (data && data.error) || err?.message || 'Upload failed' };
      }
      return { ok: true };
    } catch (uploadErr) {
      return { ok: false, msg: uploadErr?.message || 'unknown error' };
    }
  };

  // Upload (or replace) the GST certificate — edit mode's immediate action,
  // independent of the text-field Save.
  const handleUploadCert = async () => {
    if (!certFile || !modal || modal.mode !== 'edit') return;
    setUploadingCert(true);
    setCertError('');
    const result = await uploadCertForPartner(modal.id);
    if (!result.ok) {
      showToast(`Certificate upload failed: ${result.msg}`);
      setUploadingCert(false);
      return;
    }
    setEditHasCert(true);
    setCertFile(null);
    showToast('GST certificate uploaded.');
    load();
    setUploadingCert(false);
  };

  // Add an agent — immediate insert, independent of the main partner Save.
  const handleAddAgent = async () => {
    if (!modal || modal.mode !== 'edit') return;
    const name = agentName.trim();
    const phone = agentPhone.trim(); // already digits-only via the input handler
    if (!name || !phone) return;
    setAddingAgent(true);
    const { error: err } = await supabase
      .from('partner_agents')
      .insert({ partner_id: modal.id, agent_name: name, agent_phone: phone, is_active: true });
    setAddingAgent(false);
    if (err) {
      console.error('partner_agents insert failed:', err);
      showToast(`Could not add agent: ${err.message}`);
      return;
    }
    setAgentName('');
    setAgentPhone('');
    await loadAgents(modal.id);
    showToast('Agent added.');
  };

  // Remove an agent — soft delete (is_active=false) so any booking history that
  // references the agent is preserved. The list only shows active agents.
  const handleRemoveAgent = async (agent) => {
    if (!modal || modal.mode !== 'edit') return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${agent.agent_name}?`)) return;
    setRemovingAgentId(agent.id);
    const { error: err } = await supabase
      .from('partner_agents')
      .update({ is_active: false })
      .eq('id', agent.id);
    setRemovingAgentId(null);
    if (err) {
      console.error('partner_agents soft-delete failed:', err);
      showToast(`Could not remove agent: ${err.message}`);
      return;
    }
    await loadAgents(modal.id);
    showToast('Agent removed.');
  };

  // Delete is only ever allowed for a record with nothing attached. The DB
  // would otherwise SET NULL every booking's partner_id (dropping them out of
  // the partner ledger and the overdue-balance job), cascade-delete agents and
  // documents, and leave invoices / credit notes / payments pointing at nothing.
  const openDelete = async (partner) => {
    setConfirmDelete(partner);
    setDeleteCheck(null);
    const a = await loadAttachments(partner);
    setDeleteCheck(a);
  };

  const handleDelete = async () => {
    if (!confirmDelete || !deleteCheck || hasAttachments(deleteCheck)) return;
    const { error: err } = await supabase.from('partners').delete().eq('id', confirmDelete.id);
    if (err) { setError('Failed to delete partner'); setConfirmDelete(null); return; }
    setConfirmDelete(null);
    load();
  };

  // Archive: sets archived_at + archived_by and NOTHING else. No WhatsApp, no
  // booking, payment or status change. The name leaves every picker; linked
  // history stays linked by id.
  const openArchive = async (partner) => {
    setArchiveModal(partner);
    setArchiveCheck(null);
    const a = await loadAttachments(partner);
    setArchiveCheck(a);
  };

  const handleArchive = async () => {
    if (!archiveModal) return;
    setArchiveSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || null;
    const { error: err } = await supabase
      .from('partners')
      .update({ archived_at: new Date().toISOString(), archived_by: email })
      .eq('id', archiveModal.id);
    setArchiveSaving(false);
    if (err) { showToast('Failed to archive partner.'); return; }
    const name = archiveModal.name;
    setArchiveModal(null);
    setArchiveCheck(null);
    await load();
    showToast(`${name} archived.`);
  };

  const handleUnarchive = async (partner) => {
    setUnarchivingId(partner.id);
    const { error: err } = await supabase
      .from('partners')
      .update({ archived_at: null, archived_by: null })
      .eq('id', partner.id);
    setUnarchivingId(null);
    if (err) { showToast('Failed to unarchive partner.'); return; }
    await load();
    showToast(`${partner.name} restored.`);
  };

  const handleSavePref = async () => {
    if (!prefModal) return;
    setPrefSaving(true);
    const { error: err } = await supabase
      .from('partners')
      .update({
        preferred_payment_mode: prefForm.preferred_payment_mode || null,
        preferred_payment_status: prefForm.preferred_payment_status || null,
      })
      .eq('id', prefModal.id);
    if (!err) {
      setPartners((prev) =>
        prev.map((p) =>
          p.id === prefModal.id
            ? { ...p, preferred_payment_mode: prefForm.preferred_payment_mode || null, preferred_payment_status: prefForm.preferred_payment_status || null }
            : p
        )
      );
      setPrefModal(null);
    }
    setPrefSaving(false);
  };

  // View a private uploaded document: look up its storage path, mint a short-lived
  // signed URL, and open it in a new tab. Errors surface as a toast.
  const viewDocument = async (partner, docType, label) => {
    const { data: docRow, error: docErr } = await supabase
      .from('partner_documents')
      .select('file_path')
      .eq('partner_id', partner.id)
      .eq('doc_type', docType)
      .order('uploaded_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (docErr) {
      console.error('partner_documents lookup failed:', docErr);
      showToast(`Could not look up ${label}: ${docErr.message}`);
      return;
    }
    if (!docRow || !docRow.file_path) {
      showToast(`${label} not found for this partner.`);
      return;
    }
    // createSignedUrl expects the path WITHIN the bucket exactly as stored
    // (e.g. "<partner_id>/gst_cert.pdf") — never prefixed with the bucket name
    // or a leading slash, either of which yields a 400. Strip a stray leading
    // slash defensively but otherwise pass file_path through untouched.
    const path = docRow.file_path.replace(/^\/+/, '');
    const { data: signed, error: signErr } = await supabase
      .storage
      .from('partner-documents')
      .createSignedUrl(path, 60);
    if (signErr || !signed || !signed.signedUrl) {
      console.error('createSignedUrl failed for path:', path, signErr);
      showToast(`Could not open ${label}: ${signErr?.message || 'unknown error'}`);
      return;
    }
    window.open(signed.signedUrl, '_blank');
  };

  // Invite an approved partner to the partner portal. The edge function creates /
  // links their auth user and emails a set-password link (redirectTo lands them on
  // the PUBLIC site's /partner/set-password). On success the row gets an
  // auth_user_id, so we reload to flip the card to "Portal access active".
  const handleInvite = async (partner) => {
    if (!partner.email) return; // guarded by UI, but never invite without an email
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Invite ${partner.name} to the partner portal? An email will be sent to ${partner.email} to set their password.`)) return;
    setInvitingId(partner.id);
    let data, err;
    try {
      ({ data, error: err } = await supabase.functions.invoke('partner-invite', {
        body: { partnerId: partner.id, redirectTo: PARTNER_SET_PASSWORD_URL },
      }));
    } catch (invokeErr) {
      err = invokeErr;
    }
    setInvitingId(null);
    if (err || (data && data.error)) {
      const msg = (data && data.error) || err?.message || 'Failed to send invite.';
      showToast(`Could not invite ${partner.name}: ${msg}`);
      return;
    }
    await load();
    showToast(`Invite sent to ${data?.email || partner.email}.`);
  };

  const handleApprove = async (partner) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Approve ${partner.name}? They'll be able to log in and book at partner rates.`)) return;
    setApprovingId(partner.id);
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || null;
    const nowIso = new Date().toISOString();
    const { error: err } = await supabase
      .from('partners')
      .update({
        approval_status: 'approved',
        approved_by: email,
        approved_at: nowIso,
        updated_at: nowIso,
      })
      .eq('id', partner.id);
    setApprovingId(null);
    if (err) { showToast('Failed to approve partner.'); return; }
    // Send the partner an "approved" WhatsApp. Non-fatal: the approval is already
    // committed above, so a notification failure must not block or undo it.
    try {
      await supabase.functions.invoke('partner-notify', { body: { type: 'approved', partnerId: partner.id } });
    } catch (notifyErr) {
      console.error('partner-notify (approved) failed:', notifyErr);
    }
    await load();
    showToast(`${partner.name} approved.`);
  };

  const openReject = (partner) => {
    setRejectReason('');
    setRejectModal(partner);
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return;
    setRejectSaving(true);
    const nowIso = new Date().toISOString();
    const { error: err } = await supabase
      .from('partners')
      .update({
        approval_status: 'rejected',
        rejection_reason: rejectReason.trim(),
        updated_at: nowIso,
      })
      .eq('id', rejectModal.id);
    setRejectSaving(false);
    if (err) { showToast('Failed to reject partner.'); return; }
    // Send the partner a "rejected" WhatsApp with the saved reason. Non-fatal:
    // the rejection is already committed above, so a notification failure must
    // not block or undo it.
    try {
      await supabase.functions.invoke('partner-notify', { body: { type: 'rejected', partnerId: rejectModal.id, reason: rejectReason.trim() } });
    } catch (notifyErr) {
      console.error('partner-notify (rejected) failed:', notifyErr);
    }
    setRejectModal(null);
    setRejectReason('');
    await load();
    showToast('Partner rejected.');
  };

  // Suspend an approved partner — silent, internal-only. No reason captured and
  // NO partner notification (intentionally, unlike approve/reject). Just flips
  // approval_status to 'suspended' so they lose portal access until unsuspended.
  const handleSuspend = async (partner) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Suspend ${partner.name}? They won't be able to log in to the partner portal until unsuspended.`)) return;
    setSuspendingId(partner.id);
    const { error: err } = await supabase
      .from('partners')
      .update({ approval_status: 'suspended', updated_at: new Date().toISOString() })
      .eq('id', partner.id);
    setSuspendingId(null);
    if (err) { showToast('Failed to suspend partner.'); return; }
    await load();
    showToast(`${partner.name} suspended.`);
  };

  // Unsuspend — restore a suspended partner to 'approved'. Silent, no notification.
  const handleUnsuspend = async (partner) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Unsuspend ${partner.name}? They'll regain portal access.`)) return;
    setSuspendingId(partner.id);
    const { error: err } = await supabase
      .from('partners')
      .update({ approval_status: 'approved', updated_at: new Date().toISOString() })
      .eq('id', partner.id);
    setSuspendingId(null);
    if (err) { showToast('Failed to unsuspend partner.'); return; }
    await load();
    showToast(`${partner.name} unsuspended.`);
  };

  // Open the read-only "Bookings" modal for a partner. Loads that partner's
  // bookings (matched by partner_id ONLY — the Phase 3 link) most-recent-first,
  // plus the partner's agents so agent_id can be resolved to an agent name.
  const openBookings = async (partner) => {
    setBookingsModal(partner);
    setBookingsList([]);
    setBookingsAgentNames({});
    setBookingsLoading(true);
    const [bookingsRes, agentsRes] = await Promise.all([
      supabase
        .from('bookings')
        .select('*')
        .eq('partner_id', partner.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('partner_agents')
        .select('id, agent_name')
        .eq('partner_id', partner.id),
    ]);
    if (bookingsRes.error) {
      showToast(`Could not load bookings: ${bookingsRes.error.message}`);
      setBookingsLoading(false);
      return;
    }
    const nameMap = {};
    (agentsRes.data || []).forEach((a) => { nameMap[a.id] = a.agent_name; });
    setBookingsAgentNames(nameMap);
    setBookingsList(bookingsRes.data || []);
    setBookingsLoading(false);
  };

  // Status tabs count LIVE records only; archived records live in their own tab
  // whatever their approval_status.
  const livePartners = partners.filter((p) => !isArchived(p));
  const counts = {
    all: livePartners.length,
    pending: livePartners.filter((p) => effStatus(p) === 'pending').length,
    approved: livePartners.filter((p) => effStatus(p) === 'approved').length,
    suspended: livePartners.filter((p) => effStatus(p) === 'suspended').length,
    rejected: livePartners.filter((p) => effStatus(p) === 'rejected').length,
    archived: partners.length - livePartners.length,
  };

  const filteredPartners = partners.filter((p) => {
    if (statusFilter === 'archived') { if (!isArchived(p)) return false; }
    else {
      if (isArchived(p)) return false;
      if (statusFilter !== 'all' && effStatus(p) !== statusFilter) return false;
    }
    if (search.trim() && !p.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  // Shared Archive button: same affordance on every card.
  const archiveButton = (p) => (
    <button
      className="btn btn-outline btn-sm"
      onClick={() => openArchive(p)}
      title="Hide this partner from every picker; linked bookings and payments stay linked"
    >
      <Archive size={15} strokeWidth={1.75} aria-hidden="true" />Archive
    </button>
  );

  const renderPendingCard = (p) => {
    const hasTradeLicense = (docsByPartner[p.id] || []).some((d) => d.doc_type === 'trade_license');
    return (
      <div key={p.id} className="card" style={{ padding: '16px 20px', borderLeft: '3px solid var(--color-warning-strong)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: '0.97rem', color: NAVY }}>{p.name}</div>
          <span className="badge badge-amber" style={{ flexShrink: 0 }}>Pending review</span>
        </div>

        <div style={{ marginBottom: 12 }}>
          {p.contact_person && (
            <div className="detail-row">
              <span className="detail-row-label">Contact</span>
              <span className="detail-row-value">{p.contact_person}</span>
            </div>
          )}
          {p.email && (
            <div className="detail-row">
              <span className="detail-row-label">Email</span>
              <span className="detail-row-value">{p.email}</span>
            </div>
          )}
          {p.phone_number && (
            <div className="detail-row">
              <span className="detail-row-label">Phone</span>
              <span className="detail-row-value">{p.phone_number}</span>
            </div>
          )}
          {p.gstin && (
            <div className="detail-row">
              <span className="detail-row-label">GSTIN</span>
              <span className="detail-row-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.gstin}</span>
            </div>
          )}
          {p.gst_legal_name && (
            <div className="detail-row">
              <span className="detail-row-label">Legal Name</span>
              <span className="detail-row-value">{p.gst_legal_name}</span>
            </div>
          )}
          {p.gst_address && (
            <div className="detail-row">
              <span className="detail-row-label">Address</span>
              <span className="detail-row-value">{p.gst_address}</span>
            </div>
          )}
          {p.state && (
            <div className="detail-row">
              <span className="detail-row-label">State</span>
              <span className="detail-row-value">{p.state}{p.state_code ? ` (${p.state_code})` : ''}</span>
            </div>
          )}
        </div>

        {/* Document viewers */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={() => viewDocument(p, 'gst_cert', 'GST Certificate')}>
            <FileText size={15} strokeWidth={1.75} aria-hidden="true" />View GST Certificate
          </button>
          {hasTradeLicense && (
            <button className="btn btn-outline btn-sm" onClick={() => viewDocument(p, 'trade_license', 'Trade License')}>
              <FileText size={15} strokeWidth={1.75} aria-hidden="true" />View Trade License
            </button>
          )}
        </div>

        {/* Approve / Reject actions */}
        <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
          <button
            className="btn btn-gold btn-sm"
            style={{ flex: 1 }}
            onClick={() => handleApprove(p)}
            disabled={approvingId === p.id}
          >
            {approvingId === p.id ? 'Approving…' : 'Approve'}
          </button>
          <button
            className="btn btn-danger btn-sm"
            style={{ flex: 1 }}
            onClick={() => openReject(p)}
            disabled={approvingId === p.id}
          >
            Reject
          </button>
          {archiveButton(p)}
        </div>
      </div>
    );
  };

  const renderRejectedCard = (p) => (
    <div key={p.id} className="card" style={{ padding: '14px 20px', opacity: 0.72 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-ink-muted)' }}>{p.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span className="badge badge-red">Rejected</span>
          {archiveButton(p)}
        </div>
      </div>
      {p.rejection_reason && (
        <div className="detail-row" style={{ marginTop: 6 }}>
          <span className="detail-row-label">Reason</span>
          <span className="detail-row-value" style={{ color: 'var(--color-ink-muted)', fontWeight: 400 }}>{p.rejection_reason}</span>
        </div>
      )}
    </div>
  );

  const renderSuspendedCard = (p) => (
    <div key={p.id} className="card" style={{ padding: '16px 20px', borderLeft: '3px solid var(--color-warning-strong)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: '0.97rem', color: NAVY }}>{p.name}</span>
            <span className="badge badge-amber">Suspended</span>
          </div>
          {p.phone_number && (
            <div className="detail-row">
              <span className="detail-row-label">Phone</span>
              <span className="detail-row-value">{p.phone_number}</span>
            </div>
          )}
          {p.email && (
            <div className="detail-row">
              <span className="detail-row-label">Email</span>
              <span className="detail-row-value">{p.email}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button className="btn btn-outline btn-sm" onClick={() => openBookings(p)}>Bookings</button>
          <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>Edit</button>
          <button
            className="btn btn-gold btn-sm"
            onClick={() => handleUnsuspend(p)}
            disabled={suspendingId === p.id}
          >
            {suspendingId === p.id ? 'Unsuspending…' : 'Unsuspend'}
          </button>
          {archiveButton(p)}
        </div>
      </div>
    </div>
  );

  const renderApprovedCard = (p) => (
    <div key={p.id} className="card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: '0.97rem', color: NAVY }}>{p.name}</span>
            {p.approval_status === 'approved' && <span className="badge badge-green">Approved</span>}
          </div>
          {p.phone_number && (
            <div className="detail-row">
              <span className="detail-row-label">Phone</span>
              <span className="detail-row-value">{p.phone_number}</span>
            </div>
          )}
          {p.email && (
            <div className="detail-row">
              <span className="detail-row-label">Email</span>
              <span className="detail-row-value">{p.email}</span>
            </div>
          )}
          {p.gstin && (
            <div className="detail-row">
              <span className="detail-row-label">GSTIN</span>
              <span className="detail-row-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.gstin}</span>
            </div>
          )}
          {p.notes && (
            <div className="detail-row" style={{ marginTop: 2 }}>
              <span className="detail-row-label">Notes</span>
              <span className="detail-row-value" style={{ color: 'var(--color-ink-muted)', fontStyle: 'italic', fontWeight: 400 }}>
                {p.notes}
              </span>
            </div>
          )}
          {p.preferred_payment_mode && (
            <div className="detail-row">
              <span className="detail-row-label">Pref. Pay</span>
              <span className="detail-row-value">{p.preferred_payment_mode}{p.preferred_payment_status ? ` · ${p.preferred_payment_status}` : ''}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => { setPrefModal(p); setPrefForm({ preferred_payment_mode: p.preferred_payment_mode || '', preferred_payment_status: p.preferred_payment_status || '' }); }}
            title="Payment preferences"
          >
            <Settings size={16} strokeWidth={1.75} aria-hidden="true" />Prefs
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => openBookings(p)}>Bookings</button>
          <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>Edit</button>
          <button
            className="btn btn-outline btn-sm"
            style={{ color: 'var(--color-warning-strong)', borderColor: 'var(--color-warning-strong)' }}
            onClick={() => handleSuspend(p)}
            disabled={suspendingId === p.id}
            title="Temporarily disable this partner's portal access"
          >
            {suspendingId === p.id ? 'Suspending…' : 'Suspend'}
          </button>
          {archiveButton(p)}
          <button className="btn btn-danger btn-sm" onClick={() => openDelete(p)}>Delete</button>
        </div>
      </div>

      {/* Partner portal status / invite. auth_user_id set = already has login;
          no email = must add one via Edit before we can invite. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
        marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)',
      }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-ink-muted)', letterSpacing: '0.2px' }}>
          Partner Portal
        </span>
        {p.auth_user_id ? (
          <span className="badge badge-green" style={{ opacity: 0.75 }}>Portal access active</span>
        ) : !p.email ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '0.74rem', color: 'var(--color-ink-faint)' }}>Add an email first</span>
            <button className="btn btn-outline btn-sm" disabled title="Add an email via Edit before inviting">
              Invite to Portal
            </button>
          </div>
        ) : (
          <button
            className="btn btn-gold btn-sm"
            onClick={() => handleInvite(p)}
            disabled={invitingId === p.id}
          >
            {invitingId === p.id ? 'Inviting…' : 'Invite to Portal'}
          </button>
        )}
      </div>
    </div>
  );

  const renderArchivedCard = (p) => {
    const status = effStatus(p);
    const statusBadge = status === 'approved' ? <span className="badge badge-green" style={{ opacity: 0.7 }}>Approved</span>
      : status === 'pending' ? <span className="badge badge-amber" style={{ opacity: 0.7 }}>Pending review</span>
      : status === 'rejected' ? <span className="badge badge-red" style={{ opacity: 0.7 }}>Rejected</span>
      : <span className="badge badge-amber" style={{ opacity: 0.7 }}>Suspended</span>;
    return (
      <div key={p.id} className="card" style={{ padding: '14px 20px', opacity: 0.8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-ink-muted)' }}>{p.name}</span>
              <span className="badge badge-grey">Archived</span>
              {statusBadge}
            </div>
            {p.phone_number && (
              <div className="detail-row">
                <span className="detail-row-label">Phone</span>
                <span className="detail-row-value">{p.phone_number}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-row-label">Archived</span>
              <span className="detail-row-value" style={{ color: 'var(--color-ink-muted)', fontWeight: 400 }}>
                {fmtWhen(p.archived_at)}{p.archived_by ? ` by ${p.archived_by}` : ''}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn btn-outline btn-sm" onClick={() => openBookings(p)}>Bookings</button>
            <button
              className="btn btn-gold btn-sm"
              onClick={() => handleUnarchive(p)}
              disabled={unarchivingId === p.id}
              title="Put this partner back in the pickers"
            >
              <ArchiveRestore size={15} strokeWidth={1.75} aria-hidden="true" />
              {unarchivingId === p.id ? 'Restoring…' : 'Unarchive'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCard = (p) => {
    if (isArchived(p)) return renderArchivedCard(p);
    const status = effStatus(p);
    if (status === 'pending') return renderPendingCard(p);
    if (status === 'rejected') return renderRejectedCard(p);
    if (status === 'suspended') return renderSuspendedCard(p);
    return renderApprovedCard(p);
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 2 }}>Partners</h2>
          <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
            {loading ? '—' : `${livePartners.length} partner${livePartners.length !== 1 ? 's' : ''}${counts.archived ? ` · ${counts.archived} archived` : ''}`}
          </p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={openAdd}>+ Add Partner</button>
      </div>

      {/* Status filter tabs — own styling (NOT .home-tab-switcher, which is
          hidden on desktop via a media query). Always visible, all viewports. */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 16,
          padding: 4,
          background: 'var(--color-sunken)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        {STATUS_TABS.map((t) => {
          const active = statusFilter === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setStatusFilter(t.key)}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                border: active ? '1px solid #C9A84C' : '1px solid transparent',
                background: active ? '#ffffff' : 'transparent',
                color: active ? NAVY : 'var(--color-ink-muted)',
                fontSize: '0.85rem',
                fontWeight: active ? 700 : 600,
                letterSpacing: '0.2px',
                cursor: 'pointer',
                boxShadow: active ? '0 0 0 3px #C9A84C22, var(--shadow-card)' : 'none',
                transition: 'background-color 0.15s ease-out, color 0.15s ease-out, box-shadow 0.15s ease-out',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = NAVY; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = 'var(--color-ink-muted)'; }}
            >
              {t.label} ({counts[t.key]})
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <span style={{
          position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', color: 'var(--color-ink-faint)', pointerEvents: 'none',
        }}><Search size={16} strokeWidth={1.75} aria-hidden="true" /></span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search partners…"
          style={{
            width: '100%',
            background: 'var(--color-sunken)',
            border: '1.5px solid var(--color-border-strong)',
            borderRadius: 8,
            padding: '9px 32px 9px 34px',
            color: '#0A1628',
            fontSize: '0.88rem',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#C9A84C'; e.target.style.boxShadow = '0 0 0 3px #C9A84C22'; }}
          onBlur={(e) => { e.target.style.borderColor = 'var(--color-border-strong)'; e.target.style.boxShadow = 'none'; }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-ink-faint)', fontSize: '1rem', lineHeight: 1, padding: 2 }}
          >×</button>
        )}
      </div>

      {error && <div className="toast toast-error">{error}</div>}
      {toast && <div className="toast toast-success">{toast}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--color-ink-muted)', padding: '48px 0', fontSize: '0.9rem' }}>Loading...</div>
      ) : partners.length === 0 ? (
        <div style={{
          textAlign: 'center', color: 'var(--color-ink-muted)', padding: '48px 0',
          background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: 14,
        }}>
          <div style={{ marginBottom: 10 }}><Users size={32} strokeWidth={1.75} aria-hidden="true" /></div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: NAVY, marginBottom: 4 }}>No partners yet</div>
          <div style={{ fontSize: '0.8rem' }}>Add your first travel agency or partner contact.</div>
        </div>
      ) : filteredPartners.length === 0 ? (
        <div style={{
          textAlign: 'center', color: 'var(--color-ink-muted)', padding: '36px 0',
          background: '#ffffff', border: '1px solid var(--color-border)', borderRadius: 14,
        }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: NAVY }}>No partners found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredPartners.map((p) => renderCard(p))}
        </div>
      )}

      {/* Add / Edit Modal — no backdrop-click close; X/Cancel only. Partner
          entry is long enough that a stray outside click losing it all hurts. */}
      {modal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16,
            width: '100%', maxWidth: 800,
            maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
          }}>
            {/* Header — fixed */}
            <div style={{ flexShrink: 0, padding: '24px 24px 16px', fontWeight: 800, fontSize: '1.05rem', color: NAVY }}>
              {modal.mode === 'add' ? 'Add Partner' : 'Edit Partner'}
            </div>

            {/* Body — scrolls when content exceeds the height cap. Fields lay out
                in a responsive two-column grid (.form-grid) on desktop, collapsing
                to a single column at ≤500px; longer content spans both columns. */}
            <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Core fields + commercial terms */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Name <span className="required">*</span></label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Partner or agency name"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                    placeholder="e.g. +91 98765 43210"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="e.g. agent@travelco.com"
                  />
                </div>
                {/* TAC Rate — commercial commission term. Whole number 0–50; saved
                    as a number to partners.tac_rate with the main Save. */}
                <div className="form-group">
                  <label>TAC Rate (%)</label>
                  <input
                    value={form.tac_rate}
                    onChange={(e) => setForm({ ...form, tac_rate: e.target.value.replace(/\D/g, '') })}
                    placeholder="10"
                    inputMode="numeric"
                    maxLength={2}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  />
                  {tacRateValid(form.tac_rate) ? (
                    <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', marginTop: 4 }}>
                      Commission rate for this partner (0–50%). Default is 10%.
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.72rem', color: 'var(--color-danger)', marginTop: 4 }}>
                      Enter a whole number between 0 and 50.
                    </div>
                  )}
                </div>
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Any additional info..."
                    rows={3}
                  />
                </div>
              </div>

              {/* GST Details — all optional. GSTIN is format-validated only when
                  non-empty; the certificate upload is a separate action from
                  saving these text fields. */}
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16, marginTop: 2 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: NAVY, marginBottom: 4, letterSpacing: '0.2px' }}>
                  GST Details
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', marginBottom: 12 }}>
                  Optional — leave blank if the partner is not GST-registered.
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>GSTIN</label>
                    <input
                      value={form.gstin}
                      onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                      placeholder="e.g. 18ABCDE1234F1Z5"
                      maxLength={15}
                      style={{ textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.4px' }}
                    />
                    {form.gstin.trim() && !GSTIN_RE.test(form.gstin.trim().toUpperCase()) && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-danger)', marginTop: 4 }}>
                        Enter a valid 15-character GSTIN, or leave blank.
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <select
                      value={form.state}
                      onChange={(e) => {
                        const name = e.target.value;
                        const found = GST_STATES.find((s) => s.name === name);
                        setForm({ ...form, state: name, state_code: found ? found.code : '' });
                      }}
                    >
                      <option value="">Select state…</option>
                      {GST_STATES.map((s) => (
                        <option key={s.code} value={s.name}>{s.name} ({s.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>GST Legal Name</label>
                    <input
                      value={form.gst_legal_name}
                      onChange={(e) => setForm({ ...form, gst_legal_name: e.target.value })}
                      placeholder="Registered legal name"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>GST Address</label>
                    <textarea
                      value={form.gst_address}
                      onChange={(e) => setForm({ ...form, gst_address: e.target.value })}
                      placeholder="Registered address"
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      value={form.pincode}
                      onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
                      placeholder="e.g. 403001"
                      maxLength={6}
                      inputMode="numeric"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    />
                    {form.pincode.trim() && !PINCODE_RE.test(form.pincode.trim()) && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-danger)', marginTop: 4 }}>
                        Enter a valid 6-digit PIN code, or leave blank.
                      </div>
                    )}
                  </div>

                  {/* Certificate — edit mode uploads immediately via the button;
                      add mode holds the chosen file in certFile and uploads it on
                      Save, once the new partner id exists. */}
                  <div className="form-group full-width">
                    <label>GST Certificate</label>
                    {modal.mode === 'edit' && editHasCert && (
                      <div style={{ marginBottom: 8 }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => viewDocument({ id: modal.id }, 'gst_cert', 'GST Certificate')}
                        >
                          <FileText size={15} strokeWidth={1.75} aria-hidden="true" />View GST Certificate
                        </button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={onCertFileChange}
                      style={{ fontSize: '0.82rem' }}
                    />
                    {certFile && (
                      <div style={{ fontSize: '0.74rem', color: 'var(--color-ink-muted)', marginTop: 6 }}>
                        Selected: {certFile.name}
                      </div>
                    )}
                    {certError && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--color-danger)', marginTop: 6 }}>
                        {certError}
                      </div>
                    )}
                    {modal.mode === 'edit' && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ marginTop: 8 }}
                        onClick={handleUploadCert}
                        disabled={!certFile || uploadingCert}
                      >
                        {uploadingCert ? 'Uploading…' : editHasCert ? 'Replace Certificate' : 'Upload Certificate'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Agents — people at the partner company. Edit mode only (needs the
                  partner id). Add/Remove hit the DB immediately and refresh this
                  list; independent of the main partner Save (like the certificate).
                  Full-width section — the list and add row read better across the
                  whole modal than in a single column. */}
              {modal.mode === 'edit' && (
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16, marginTop: 2 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: NAVY, marginBottom: 4, letterSpacing: '0.2px' }}>
                    Agents
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-muted)', marginBottom: 12 }}>
                    People at this partner company. Added and removed immediately.
                  </div>

                  {/* Current agents */}
                  {agents.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 12 }}>
                      No agents added yet.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                      {agents.map((a) => (
                        <div
                          key={a.id}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                            padding: '8px 12px', background: 'var(--color-sunken)',
                            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.86rem', color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {a.agent_name}
                            </div>
                            <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-muted)', fontVariantNumeric: 'tabular-nums' }}>
                              {a.agent_phone}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            style={{ flexShrink: 0 }}
                            onClick={() => handleRemoveAgent(a)}
                            disabled={removingAgentId === a.id}
                          >
                            {removingAgentId === a.id ? 'Removing…' : 'Remove'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add agent — Name / Phone paired in the grid, Add button below */}
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Agent Name</label>
                      <input
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        placeholder="e.g. Priya Sharma"
                      />
                    </div>
                    <div className="form-group">
                      <label>Agent Phone</label>
                      <input
                        value={agentPhone}
                        onChange={(e) => setAgentPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g. 9876543210"
                        inputMode="numeric"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={handleAddAgent}
                        disabled={!agentName.trim() || !agentPhone.trim() || addingAgent}
                      >
                        {addingAgent ? 'Adding…' : 'Add Agent'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — pinned; Save/Cancel always reachable regardless of body length */}
            <div style={{ flexShrink: 0, padding: '14px 24px 18px', borderTop: '1px solid var(--color-border)' }}>
              {error && <div className="toast toast-error" style={{ marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                  onClick={() => setModal(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-gold"
                  style={{ flex: 1 }}
                  onClick={handleSave}
                  disabled={saving || !form.name.trim() || (form.gstin.trim() !== '' && !GSTIN_RE.test(form.gstin.trim().toUpperCase())) || !tacRateValid(form.tac_rate)}
                >
                  {saving ? 'Saving...' : modal.mode === 'add' ? 'Add Partner' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget && !rejectSaving) setRejectModal(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 420,
            boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
          }}>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: NAVY, marginBottom: 4 }}>
              Reject Partner
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>{rejectModal.name}</div>

            <div className="form-group">
              <label>Rejection Reason <span className="required">*</span></label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this registration is being rejected…"
                rows={4}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setRejectModal(null)} disabled={rejectSaving}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                style={{ flex: 1 }}
                onClick={handleReject}
                disabled={rejectSaving || !rejectReason.trim()}
              >
                {rejectSaving ? 'Rejecting…' : 'Reject Partner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {prefModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget && !prefSaving) setPrefModal(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 380,
            boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
          }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>
              Payment Preferences
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>{prefModal.name}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label>Preferred Payment Mode</label>
                <select
                  value={prefForm.preferred_payment_mode}
                  onChange={(e) => setPrefForm((f) => ({ ...f, preferred_payment_mode: e.target.value }))}
                >
                  <option value="">None</option>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Preferred Payment Status</label>
                <select
                  value={prefForm.preferred_payment_status}
                  onChange={(e) => setPrefForm((f) => ({ ...f, preferred_payment_status: e.target.value }))}
                >
                  <option value="">None</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setPrefModal(null)} disabled={prefSaving}>
                Cancel
              </button>
              <button className="btn btn-gold" style={{ flex: 1 }} onClick={handleSavePref} disabled={prefSaving}>
                {prefSaving ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation. Delete stays disabled until the attachment check
          comes back clean; anything linked means Archive is the right tool. */}
      {confirmDelete && (() => {
        const checking = deleteCheck === null;
        const blocked = !checking && hasAttachments(deleteCheck);
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 16,
            }}
          >
            <div style={{
              background: '#fff', borderRadius: 16, padding: 24,
              width: '100%', maxWidth: 400,
              boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
              textAlign: 'center',
            }}>
              <div style={{ marginBottom: 12 }}><Trash2 size={32} strokeWidth={1.75} aria-hidden="true" /></div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>Delete Partner?</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', marginBottom: 14 }}>{confirmDelete.name}</div>
              {checking ? (
                <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)', marginBottom: 22 }}>Checking what is linked…</div>
              ) : blocked ? (
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--color-danger-strong)', marginBottom: 8 }}>
                    Cannot delete: this partner has history.
                  </div>
                  <AttachmentSummary a={deleteCheck} verb="would be unlinked" />
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginTop: 10 }}>
                    Deleting would unlink every booking and drop them out of the partner ledger. Archive it instead: the name leaves the pickers and the history stays linked.
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', marginBottom: 22 }}>
                  Nothing is linked to this partner. This cannot be undone.
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
                {blocked ? (
                  <button className="btn btn-gold" style={{ flex: 1 }} onClick={() => { const p = confirmDelete; setConfirmDelete(null); openArchive(p); }}>
                    Archive instead
                  </button>
                ) : (
                  <button className="btn btn-danger" style={{ flex: 1 }} onClick={handleDelete} disabled={checking}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Archive Confirmation: shows what stays linked, never blocks. */}
      {archiveModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget && !archiveSaving) setArchiveModal(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 420,
            boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
            textAlign: 'center',
          }}>
            <div style={{ marginBottom: 12 }}><Archive size={32} strokeWidth={1.75} aria-hidden="true" /></div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>Archive Partner?</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', marginBottom: 14 }}>{archiveModal.name}</div>
            <div style={{ fontSize: '0.82rem', color: NAVY, marginBottom: 12, textAlign: 'left' }}>
              The name disappears from every partner picker and will not be matched when typed. Nothing else changes: no message is sent, and no booking, payment or approval status is touched. You can unarchive it later from the Archived tab.
            </div>
            <div style={{ background: 'var(--color-sunken)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 20, textAlign: 'left' }}>
              {archiveCheck === null
                ? <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)' }}>Checking what is linked…</div>
                : <AttachmentSummary a={archiveCheck} verb="will stay linked" />}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setArchiveModal(null)} disabled={archiveSaving}>
                Cancel
              </button>
              <button className="btn btn-gold" style={{ flex: 1 }} onClick={handleArchive} disabled={archiveSaving}>
                {archiveSaving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partner Bookings — read-only list, matched by partner_id (Phase 3 link).
          Scrollable body with a sticky header + footer so Close is always reachable. */}
      {bookingsModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setBookingsModal(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16,
            width: '100%', maxWidth: 700,
            maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(10,22,40,0.22)',
          }}>
            {/* Header — fixed */}
            <div style={{
              flexShrink: 0, padding: '20px 24px 14px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '1.05rem', color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {bookingsModal.name} — Bookings
                </div>
                {!bookingsLoading && (
                  <div style={{ fontSize: '0.76rem', color: 'var(--color-ink-muted)', marginTop: 2 }}>
                    {bookingsList.length} booking{bookingsList.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} onClick={() => setBookingsModal(null)}>Close</button>
            </div>

            {/* Body — scrolls */}
            <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '16px 24px' }}>
              {bookingsLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--color-ink-muted)', padding: '40px 0', fontSize: '0.9rem' }}>
                  Loading…
                </div>
              ) : bookingsList.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--color-ink-muted)', padding: '40px 0', fontSize: '0.9rem' }}>
                  No bookings yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {bookingsList.map((b) => {
                    const pax = (Number(b.adults) || 0) + (Number(b.children) || 0);
                    const paxLabel = `${pax} pax${Number(b.infants) > 0 ? ` + ${b.infants} inf` : ''}`;
                    const agentName = b.agent_id ? (bookingsAgentNames[b.agent_id] || '—') : '—';
                    const money = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN')}`;
                    const statusClass =
                      b.payment_status === 'Confirmed' ? 'badge badge-green'
                      : b.payment_status === 'Cancelled' ? 'badge badge-red'
                      : 'badge badge-amber';
                    return (
                      <div key={b.id} className="card" style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: NAVY, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {b.booking_name || '—'}
                          </div>
                          {b.payment_status && (
                            <span className={statusClass} style={{ flexShrink: 0 }}>{b.payment_status}</span>
                          )}
                        </div>
                        <div className="detail-row">
                          <span className="detail-row-label">Date</span>
                          <span className="detail-row-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.date || '—'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-row-label">Cruise</span>
                          <span className="detail-row-value">{b.cruise_type || '—'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-row-label">Pax</span>
                          <span className="detail-row-value">{paxLabel}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-row-label">Agent</span>
                          <span className="detail-row-value">{agentName}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-row-label">Total</span>
                          <span className="detail-row-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {money(b.total_amount)}
                            <span style={{ color: 'var(--color-ink-muted)', fontWeight: 400 }}>
                              {' '}· TAC {money(b.tac)} · Net {money(b.net_total)}
                            </span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — pinned; Close always reachable */}
            <div style={{ flexShrink: 0, padding: '14px 24px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setBookingsModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
